import {
  type CreateMaintenanceTemplateRequest,
  createMaintenanceTemplateRequestSchema,
  emptyMaintenanceChecklistDefinition,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type Resolver, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { MaintenanceFormPage } from '@/products/maintenance/features/maintenance-form-page';
import { ChecklistPreview } from '@/products/maintenance/features/protocol-renderer/checklist-preview';
import {
  templateNeedsPdf,
  templatePdfPreviewText,
} from '@/products/maintenance/features/templates/template-kind-label';
import { useCreateMaintenanceTemplate } from '@/products/maintenance/features/use-maintenance-templates';
import {
  MAINTENANCE_TEMPLATES_PATH,
  maintenanceTemplatePath,
} from '@/products/maintenance/maintenance-paths';

const defaultValues: CreateMaintenanceTemplateRequest = {
  name: '',
  kind: 'pdf_overlay',
  description: null,
};

const emptyChecklist = emptyMaintenanceChecklistDefinition();

export function MaintenanceTemplateFormPage() {
  usePageTitle('Vorlage anlegen');
  const navigate = useNavigate();
  const create = useCreateMaintenanceTemplate();

  const form = useForm<CreateMaintenanceTemplateRequest>({
    resolver: zodResolver(
      createMaintenanceTemplateRequestSchema,
    ) as Resolver<CreateMaintenanceTemplateRequest>,
    defaultValues,
  });
  const [path, setPath] = useState<'checklist' | 'pdf_ink' | 'pdf_acroform'>('checklist');
  const [checklistKind, setChecklistKind] = useState<'structured' | 'pdf_overlay'>('pdf_overlay');

  async function onValid(values: CreateMaintenanceTemplateRequest) {
    try {
      const created = await create.mutateAsync(values);
      toast.success('Vorlage angelegt.');
      void navigate(maintenanceTemplatePath(created.id));
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Die Vorlage konnte nicht angelegt werden.';
      toast.error(message);
    }
  }

  const errors = form.formState.errors;
  const kind = useWatch({ control: form.control, name: 'kind' });

  return (
    <FormProvider {...form}>
      <MaintenanceFormPage
        title="Vorlage anlegen"
        description="Drei Wege: eigene Checkliste, normales PDF zum Beschriften, oder eine PDF mit vorhandenen Feldern."
        cancelHref={MAINTENANCE_TEMPLATES_PATH}
        pending={create.isPending}
        onSubmit={() => {
          void form.handleSubmit(onValid)();
        }}
      >
        <div className="space-y-4">
          <h2 className="text-base font-semibold">Vorlage</h2>
          <div className="space-y-2">
            <Label htmlFor="tpl-name">Name</Label>
            <Input id="tpl-name" className="min-h-11" {...form.register('name')} />
            {errors.name?.message ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.name.message}
              </p>
            ) : null}
          </div>
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Art</legend>
            {(
              [
                ['checklist', 'Eigene Checkliste'],
                ['pdf_ink', 'Normales PDF'],
                ['pdf_acroform', 'PDF mit Feldern'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex min-h-11 items-center gap-3 text-sm">
                <input
                  type="radio"
                  name="template-path"
                  className="size-4"
                  checked={path === value}
                  onChange={() => {
                    setPath(value);
                    form.setValue('kind', value === 'checklist' ? checklistKind : value, {
                      shouldValidate: true,
                    });
                  }}
                />
                {label}
              </label>
            ))}
          </fieldset>
          {path === 'checklist' ? (
            <div className="space-y-2">
              <Label htmlFor="tpl-checklist">Checkliste</Label>
              <Select
                value={checklistKind}
                onValueChange={(value) => {
                  const next = value === 'structured' ? 'structured' : 'pdf_overlay';
                  setChecklistKind(next);
                  form.setValue('kind', next, { shouldValidate: true });
                }}
              >
                <SelectTrigger id="tpl-checklist" className="min-h-11 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="structured">Ohne PDF</SelectItem>
                  <SelectItem value="pdf_overlay">Felder selbst auf das PDF legen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-2">
            <h2 className="text-base font-semibold">Vorschau</h2>
            {templateNeedsPdf(kind) ? (
              <p className="text-sm text-muted-foreground">
                {templatePdfPreviewText('create', false)}
              </p>
            ) : (
              <ChecklistPreview definition={emptyChecklist} />
            )}
          </div>
        </div>
      </MaintenanceFormPage>
    </FormProvider>
  );
}
